import type { ClipRangeCount } from "../../../client-messages/clips";

/**
 * What the request form knows about how many clips the pending selection
 * covers.
 *
 * `unavailable` is a state rather than an error to surface: a failed count
 * must not dead-end the dialog, so the form warns and still allows the
 * request. See `useCompilationClipCount`.
 */
export type ClipCountEstimate =
  | { readonly state: "unselected" }
  | { readonly state: "counting" }
  | { readonly state: "counted"; readonly count: number; readonly totalDurationMs: number }
  | { readonly state: "unavailable" };

const MS_PER_SECOND = 1000;
const MS_PER_MINUTE = 60 * MS_PER_SECOND;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;

/**
 * How much footage a duration total covers, in the coarsest units that still
 * say something useful.
 *
 * Seconds are dropped once the total runs to hours — a compilation range spans
 * days, so at that scale they are noise, and the total is an estimate anyway.
 */
export function formatFootageDuration(totalDurationMs: number): string {
  const hours = Math.floor(totalDurationMs / MS_PER_HOUR);
  const minutes = Math.floor((totalDurationMs % MS_PER_HOUR) / MS_PER_MINUTE);
  const seconds = Math.floor((totalDurationMs % MS_PER_MINUTE) / MS_PER_SECOND);

  if (hours > 0) return minutes > 0 ? `${String(hours)} hr ${String(minutes)} min` : `${String(hours)} hr`;
  if (minutes > 0) return seconds > 0 ? `${String(minutes)} min ${String(seconds)} sec` : `${String(minutes)} min`;
  return `${String(seconds)} sec`;
}

/**
 * Resolves the query state behind a count into what the form should show.
 *
 * `settled` is what keeps a stale total off screen: the count query trails the
 * form by a debounce, so between a picker change and the next fetch the
 * previous range's total is still in hand. Showing it would gate the request
 * on a count for a range the user has already moved off.
 */
export function toClipCountEstimate(input: {
  /** Whether the form holds a complete, valid selection to count. */
  readonly selected: boolean;
  /** Whether the counted selection is the one currently in the form. */
  readonly settled: boolean;
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly count: ClipRangeCount | undefined;
}): ClipCountEstimate {
  if (!input.selected) return { state: "unselected" };
  // Settling is checked before any query state, because every query state in
  // hand belongs to the *previous* selection until the debounce lands. A
  // failed count read as `unavailable` here would carry that range's verdict
  // — which deliberately allows the request — onto a range nothing has
  // counted yet, ungating a window that may hold no clips at all.
  if (!input.settled) return { state: "counting" };
  if (input.isError) return { state: "unavailable" };
  if (input.isPending || !input.count) return { state: "counting" };
  return { state: "counted", count: input.count.count, totalDurationMs: input.count.totalDurationMs };
}

/**
 * Whether the compilation must not be requested yet.
 *
 * A count of zero blocks: the edge fails such a job terminally with
 * `no_recordings`, so there is nothing to gain from the round trip. A count
 * that could not be fetched does not block — a failing count endpoint is not a
 * reason to stop someone requesting a compilation.
 */
export function blocksCompilationRequest(estimate: ClipCountEstimate): boolean {
  switch (estimate.state) {
    case "counted":
      return estimate.count === 0;
    case "unavailable":
      return false;
    default:
      return true;
  }
}
